import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApp } from '../../context/AppContext';
import { AdaptiveModal } from '../Common/AdaptiveModal';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import { frameworksApi, formatFrameworkError, parseStoryFrameworkRef } from '../../api/frameworks';
import FrameworkEditor, { builtFrameworkSaveBody } from './FrameworkEditor';
import FrameworkCreateFlow from './FrameworkCreateFlow';
import {
  builderSessionIsLive,
  clearBuilderSession,
  readBuilderSession,
  writeBuilderSession,
} from './builderSession';
import NameCopyDialog from './NameCopyDialog';
import ThinGenreMarker, { beatCiteLabel } from './ThinGenreMarker';
import GenreStandingMark from './GenreStandingMark';
import { sharedShelfPaths, shelfLevel, splitAmazonPath } from '../../utils/amazonShelfBrowse';
import { writerFacingCatalog } from '../../utils/writerFacingCatalog';
import { outlineActHeader } from '../../utils/outlineActHeader';
import {
  ADD_SCENE_CANCEL,
  FRAMEWORK_BUILDER_DEFINITION,
  FRAMEWORK_BUILDER_HEADING,
  FRAMEWORKS_NEXT as FRAMEWORKS_NEXT_COPY,
  THIS_BECOMES_THIS_BOOKS_FRAMEWORK,
  FRAMEWORK_NOW_THIS_BOOKS,
  FRAMEWORK_NOW_USED,
  FRAMEWORK_SAVED,
  KEEP_MY_SEQUENCES,
  KEEP_MY_SEQUENCES_HELP,
  KEEP_SEQUENCES_BODY,
  KEEP_SEQUENCES_TITLE,
  USE_FRAMEWORK_SEQUENCES,
  USE_FRAMEWORK_SEQUENCES_HELP,
  USING_THIS_FRAMEWORK,
} from '../../utils/writerCopy';
import FrameworkExplainerLink from './FrameworkExplainerLink';
import HelpIcon from '../Common/HelpIcon';
import './FrameworkPicker.css';

export const EXPORT_NEEDS_CUSTOM = "Export is for a Framework you made. You don't have one yet.";
export const FRAMEWORKS_LOADING = 'Loading Frameworks…';
export const FRAMEWORKS_READY = 'The list is here.';
export const FRAMEWORKS_NEXT = FRAMEWORKS_NEXT_COPY;

export const GENRE_DRILL_INTRO = 'Start with a genre, then narrow until you reach a Framework.';
export const PICK_A_GENRE = 'Pick a genre to start.';
export const SHARED_SHELF_NOTE = 'These Frameworks share one listing. The line under each name is the difference.';
export const LISTING_EMPTY = 'No Frameworks in this listing.';
export const PREVIEW_EMPTY = 'Pick a Framework to preview sequences and use it for this book.';

const GENRE_KEY_PREFIX = 'genre:';
const CATALOG_RETRY_STATUSES = [500, 502, 503, 504];
const CATALOG_RETRY_MS = 800;

function isChooserPack(item) {
  return Boolean(item?.amazon_parent_path);
}

/** selectedId is a library key or a custom: ref from the book. */
export function matchesSelected(item, selectedId) {
  if (!item || !selectedId) return false;
  if (item.id === selectedId) return true;
  const parsed = parseStoryFrameworkRef(selectedId);
  return Boolean(parsed && parsed.id === item.id && parsed.source === item.source);
}

function genreCategoryKey(parent) {
  return `${GENRE_KEY_PREFIX}${parent}`;
}

function genreParentFromCategory(category) {
  if (typeof category !== 'string' || !category.startsWith(GENRE_KEY_PREFIX)) return null;
  return category.slice(GENRE_KEY_PREFIX.length) || null;
}

function detailFromDocsSeed(seed) {
  if (!seed || !Array.isArray(seed.beats) || !seed.beats.length) return null;
  return {
    source: 'custom',
    framework_name: seed.framework_name || '',
    description: seed.description || '',
    genre_tag: seed.genre_tag || '',
    thin_genre: Boolean(seed.thin_genre),
    beats: seed.beats.map((beat) => ({
      name: beat.name || '',
      act: beat.act || 1,
      purpose: beat.purpose || '',
      cite_kind: beat.cite_kind || 'general',
      cite_titles: beat.cite_titles || [],
    })),
    acts: (seed.acts || []).map((act) => ({
      number: act.number || act.act || 1,
      name: act.name || '',
      purpose: act.purpose || '',
    })),
    tracks: [],
    tropes: [],
  };
}

function groupBeats(beats) {
  const groups = [];
  (beats || []).forEach((beat) => {
    const last = groups[groups.length - 1];
    if (!last || last.act !== beat.act) {
      groups.push({ act: beat.act, beats: [beat] });
    } else {
      last.beats.push(beat);
    }
  });
  return groups;
}

/**
 * Shared framework picker / manager.
 *
 * mode="select" — embeddable chooser (Story Board / Plot Focus later).
 * mode="manage" — browse library, edit customs, import/export.
 */
export default function FrameworkPicker({
  isOpen,
  onClose,
  mode = 'manage',
  onSelect,
  selectedId = null,
  hangKind = null,
  selectLabel = 'Use this Framework',
  selectHint = null,
  embedded = false,
  initialEdit = null,
  openBuilder = null,
  storyId = null,
  handmadeSequenceCount = 0,
}) {
  const app = useApp() || {};
  const openBookId = storyId || app.currentStory?.id || null;
  const breakpoint = useBreakpoint();
  const [catalog, setCatalog] = useState({ library: {}, custom: [] });
  const [loading, setLoading] = useState(Boolean(isOpen));
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState(null);
  const [shelfTrail, setShelfTrail] = useState([]);
  const [view, setView] = useState(() => (
    builderSessionIsLive(readBuilderSession(storyId || app.currentStory?.id)) ? 'create' : 'browse'
  ));
  const [detail, setDetail] = useState(null);
  const [activeItem, setActiveItem] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [exportIds, setExportIds] = useState(new Set());
  const [copyTarget, setCopyTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  // Runs 40–42: Save and Use this framework said nothing where the writer clicked.
  const [notice, setNotice] = useState(null);
  const [manualBuild, setManualBuild] = useState(false);
  const [selecting, setSelecting] = useState(false);
  const [asking, setAsking] = useState(false);
  const previewReq = useRef(0);
  const selectingRef = useRef(false);
  const didAutoSelect = useRef(false);
  const openedBuilder = useRef(null);
  const shelfDefaulted = useRef(false);
  const selectedIdRef = useRef(selectedId);
  selectedIdRef.current = selectedId;
  const [sessionOpen, setSessionOpen] = useState(isOpen);
  const seedKey = initialEdit
    ? `${initialEdit.framework_name || ''}|${(initialEdit.beats || []).map((beat) => (
      `${beat.act}:${beat.name}:${beat.purpose || ''}`
    )).join('|')}`
    : '';

  const loadCatalog = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let data;
      try {
        data = await frameworksApi.catalog();
      } catch (first) {
        // A network error or 500/502/503/504 (a dead pool right after a deploy)
        // gets one more try before the writer sees an error.
        if (first?.status && !CATALOG_RETRY_STATUSES.includes(first.status)) throw first;
        await new Promise((resolve) => { setTimeout(resolve, CATALOG_RETRY_MS); });
        data = await frameworksApi.catalog();
      }
      const facing = writerFacingCatalog({
        library: data.library || {},
        custom: data.custom || [],
      });
      const library = facing.library;
      const custom = facing.custom;
      const libraryItems = Object.values(library).flat();
      const hasAmazon = libraryItems.some(isChooserPack);
      setCatalog({ library, custom });
      // Decide the entry category in this same update as the catalog so the
      // first browse paint is the genre root (or All when no Amazon packs
      // exist). A post-load effect used to paint All, then switch — that
      // flash is #1252. Broads have no Amazon path and are not a pick.
      const selectedNow = selectedIdRef.current;
      const selected = selectedNow
        ? [...custom, ...libraryItems].find((item) => item.id === selectedNow)
        : null;
      if (selected?.source === 'custom') {
        shelfDefaulted.current = true;
        setCategory('custom');
        setShelfTrail([]);
      } else if (selected && isChooserPack(selected)) {
        shelfDefaulted.current = true;
        const trail = splitAmazonPath(selected.amazon_parent_path);
        setCategory(genreCategoryKey(trail[0]));
        setShelfTrail(trail);
      } else if (selected || (!shelfDefaulted.current && !selectedNow)) {
        shelfDefaulted.current = true;
        setCategory(hasAmazon ? null : 'all');
        setShelfTrail([]);
      }
    } catch (err) {
      setError(formatFrameworkError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  // Reset before paint when the door opens. An effect would run after the
  // first open frame and flash whatever category was left from last time (All).
  if (isOpen !== sessionOpen) {
    setSessionOpen(isOpen);
    if (isOpen) {
      const seeded = seedKey ? detailFromDocsSeed(initialEdit) : null;
      if (builderSessionIsLive(readBuilderSession(openBookId))) {
        setManualBuild(false);
        setView('create');
        setDetail({ source: 'custom', beats: [] });
      } else {
        setView(seeded ? 'edit' : 'browse');
        setDetail(seeded);
      }
      setActiveItem(null);
      setCategory(null);
      setShelfTrail([]);
      setAsking(false);
      setSelecting(false);
      shelfDefaulted.current = false;
      setSearch('');
      setExportIds(new Set());
      setPreviewLoading(false);
      setError(null);
      setLoading(true);
      didAutoSelect.current = false;
    }
  }

  useEffect(() => {
    if (!isOpen) return;
    if (builderSessionIsLive(readBuilderSession(openBookId))) {
      setManualBuild(false);
      setView('create');
      setDetail({ source: 'custom', beats: [] });
      loadCatalog();
      return;
    }
    const seeded = seedKey ? detailFromDocsSeed(initialEdit) : null;
    setView(seeded ? 'edit' : 'browse');
    setDetail(seeded);
    setActiveItem(null);
    setCategory(null);
    setShelfTrail([]);
    shelfDefaulted.current = false;
    setSearch('');
    setExportIds(new Set());
    setPreviewLoading(false);
    didAutoSelect.current = false;
    loadCatalog();
  }, [isOpen, loadCatalog, seedKey, initialEdit, openBookId]);

  // Drawer "Framework Builder" lands here. /frameworks alone stays the chooser.
  useEffect(() => {
    if (!isOpen || !openBuilder || openedBuilder.current === openBuilder) return;
    openedBuilder.current = openBuilder;
    setNotice(null);
    setManualBuild(false);
    setDetail({ source: 'custom', beats: [] });
    setView('create');
  }, [isOpen, openBuilder]);

  const query = search.trim().toLowerCase();
  const matchesQuery = useCallback((item) => {
    if (!query) return true;
    return (
      item.framework_name.toLowerCase().includes(query)
      || (item.description || '').toLowerCase().includes(query)
      || (item.amazon_parent_path || '').toLowerCase().includes(query)
    );
  }, [query]);

  const customItems = useMemo(
    () => (catalog.custom || []).filter(matchesQuery),
    [catalog.custom, matchesQuery]
  );

  const allLibraryItems = useMemo(
    () => Object.values(catalog.library || {}).flat(),
    [catalog.library],
  );

  const chooserPacks = useMemo(
    () => allLibraryItems.filter(isChooserPack),
    [allLibraryItems],
  );

  const matchingPacks = useMemo(
    () => chooserPacks.filter(matchesQuery),
    [chooserPacks, matchesQuery],
  );

  const sharedPaths = useMemo(
    () => sharedShelfPaths(chooserPacks),
    [chooserPacks],
  );

  const parentTops = useMemo(
    () => shelfLevel(matchingPacks, []).children,
    [matchingPacks],
  );

  const allItems = useMemo(
    () => [...customItems, ...matchingPacks],
    [customItems, matchingPacks],
  );

  const categories = useMemo(() => {
    const cats = [
      { key: 'custom', label: 'Custom', count: customItems.length },
      { key: 'all', label: 'All', count: allItems.length },
    ];
    parentTops.forEach((parent) => {
      cats.push({
        key: genreCategoryKey(parent.label),
        label: parent.label,
        count: parent.count,
      });
    });
    return cats;
  }, [customItems.length, allItems.length, parentTops]);

  const visibleItems = useMemo(() => {
    if (category === 'custom') return customItems;
    if (category === 'all') return allItems;
    return [];
  }, [category, customItems, allItems]);

  const loadDetail = async (item) => {
    const dto = item.source === 'custom'
      ? await frameworksApi.getCustom(item.id)
      : await frameworksApi.getLibrary(item.id);
    setDetail(dto);
    return dto;
  };

  const selectPreview = useCallback(async (item) => {
    const req = ++previewReq.current;
    setAsking(false);
    setNotice(null);
    setActiveItem(item);
    setPreviewLoading(true);
    setError(null);
    try {
      const dto = item.source === 'custom'
        ? await frameworksApi.getCustom(item.id)
        : await frameworksApi.getLibrary(item.id);
      if (req !== previewReq.current) return;
      setDetail(dto);
    } catch (err) {
      if (req !== previewReq.current) return;
      setDetail(null);
      setError(formatFrameworkError(err));
    } finally {
      if (req === previewReq.current) setPreviewLoading(false);
    }
  }, []);

  const openDetail = async (item) => {
    setAsking(false);
    setError(null);
    setActiveItem(item);
    try {
      await loadDetail(item);
      setView('detail');
    } catch (err) {
      setError(formatFrameworkError(err));
    }
  };

  const handleBrowseSelect = (item) => {
    if (breakpoint === 'mobile') {
      openDetail(item);
    } else {
      selectPreview(item);
    }
  };

  useEffect(() => {
    if (!isOpen || loading || didAutoSelect.current || !selectedId) return;
    const found = [...(catalog.custom || []), ...allLibraryItems].find((item) => (
      matchesSelected(item, selectedId)
    ));
    if (!found) return;
    didAutoSelect.current = true;
    shelfDefaulted.current = true;
    if (found.source === 'custom') {
      setCategory('custom');
      setShelfTrail([]);
    } else if (isChooserPack(found)) {
      const trail = splitAmazonPath(found.amazon_parent_path);
      setCategory(genreCategoryKey(trail[0]));
      setShelfTrail(trail);
    } else {
      // Hung broad: the room header still names it. The chooser does not offer it.
      setCategory(chooserPacks.length ? null : 'all');
      setShelfTrail([]);
      return;
    }
    if (breakpoint === 'mobile') {
      setActiveItem(found);
    } else {
      selectPreview(found);
    }
  }, [isOpen, loading, selectedId, catalog.custom, allLibraryItems, chooserPacks.length, breakpoint, selectPreview]);

  const startCopy = (item) => {
    setCopyTarget(item);
  };

  const confirmCopy = async (name) => {
    if (!copyTarget) return;
    setBusy(true);
    setError(null);
    try {
      const created = copyTarget.source === 'custom'
        ? await frameworksApi.copyCustom(copyTarget.id, name)
        : await frameworksApi.copyLibrary(copyTarget.id, name);
      setCopyTarget(null);
      await loadCatalog();
      setDetail(created);
      setActiveItem(created);
      setCategory('custom');
      setView(copyTarget.source === 'library' ? 'edit' : 'browse');
    } catch (err) {
      setError(formatFrameworkError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleSave = async (body) => {
    setBusy(true);
    setError(null);
    try {
      const updating = detail?.source === 'custom' && view === 'edit' && detail.id;
      const saved = updating
        ? await frameworksApi.updateCustom(detail.id, body)
        : await frameworksApi.createCustom(body);
      await loadCatalog();
      clearBuilderSession(openBookId);
      setDetail(saved);
      setActiveItem(saved);
      setCategory('custom');
      setView('browse');
      if (!updating && onSelect) {
        const used = await onSelect(saved);
        if (!embedded) {
          onClose();
          return;
        }
        if (used !== false) {
          setNotice(hangKind === 'book'
            ? FRAMEWORK_NOW_THIS_BOOKS(saved?.framework_name)
            : FRAMEWORK_NOW_USED(saved?.framework_name));
          return;
        }
      }
      setNotice(FRAMEWORK_SAVED(saved?.framework_name));
    } catch (err) {
      setError(formatFrameworkError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete “${item.framework_name}”? This cannot be undone.`)) return;
    setBusy(true);
    setError(null);
    try {
      await frameworksApi.deleteCustom(item.id);
      await loadCatalog();
      setView('browse');
      setDetail(null);
      setActiveItem(null);
    } catch (err) {
      setError(formatFrameworkError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleExport = async () => {
    if (exportIds.size === 0) return;
    setBusy(true);
    setError(null);
    try {
      const bundle = await frameworksApi.exportSelected([...exportIds]);
      const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'custom-frameworks.json';
      a.click();
      URL.revokeObjectURL(url);
      setView('browse');
    } catch (err) {
      setError(formatFrameworkError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleImport = async (file) => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      await frameworksApi.importBundle(parsed);
      await loadCatalog();
      setView('browse');
    } catch (err) {
      if (err instanceof SyntaxError) {
        setError('That file is not valid JSON.');
      } else {
        const detail = err?.message;
        if (detail && typeof detail === 'object' && Array.isArray(detail.errors)) {
          const first = detail.errors[0];
          setError(first ? `${first.field}: ${first.message}` : 'The file could not be imported.');
        } else {
          setError(formatFrameworkError(err));
        }
      }
    } finally {
      setBusy(false);
    }
  };

  const sequenceCount = Number(handmadeSequenceCount) || 0;

  const commitSelect = async (keepParts) => {
    if (!onSelect || !detail || selectingRef.current) return;
    selectingRef.current = true;
    setAsking(false);
    setNotice(null);
    setSelecting(true);
    try {
      const result = keepParts
        ? await onSelect(detail, { keepParts: true })
        : await onSelect(detail);
      if (result === false) return;
      if (!embedded) {
        onClose();
        return;
      }
      setNotice(hangKind === 'book'
        ? FRAMEWORK_NOW_THIS_BOOKS(detail.framework_name)
        : FRAMEWORK_NOW_USED(detail.framework_name));
    } finally {
      selectingRef.current = false;
      setSelecting(false);
    }
  };

  const handleSelect = async () => {
    if (!onSelect || !detail || selecting || asking) return;
    // The Framework already on this book. Opening it again changes nothing.
    if (matchesSelected(detail, selectedId) || matchesSelected(activeItem, selectedId)) return;
    if (sequenceCount > 0) {
      setAsking(true);
      return;
    }
    await commitSelect(false);
  };

  const reservePreview = breakpoint !== 'mobile';
  const genreParent = genreParentFromCategory(category);
  const drillTrail = genreParent
    ? (shelfTrail[0] === genreParent ? shelfTrail : [genreParent])
    : [];
  const showGenreDrill = category == null || Boolean(genreParent);
  const openBuild = (manual) => {
    setNotice(null);
    setManualBuild(manual);
    setDetail({ source: 'custom', beats: [] });
    setView('create');
    if (!manual && readBuilderSession(openBookId)) {
      writeBuilderSession(openBookId, { writerClosed: false });
    }
  };
  const leaveBuild = () => {
    if (readBuilderSession(openBookId)) {
      writeBuilderSession(openBookId, { writerClosed: true });
    }
    if (activeItem?.id) selectPreview(activeItem);
    else setDetail(null);
    setView('browse');
  };
  const closePicker = useCallback(() => {
    if (readBuilderSession(openBookId)) {
      writeBuilderSession(openBookId, { writerClosed: true });
    }
    onClose();
  }, [onClose, openBookId]);
  const buildActions = (
    <div className="fw-list-actions">
      <button type="button" className="fw-btn fw-btn--ghost" onClick={() => openBuild(false)}>
        Build with help
      </button>
      <button type="button" className="fw-btn fw-btn--ghost" onClick={() => openBuild(true)}>
        Make my own Framework
      </button>
    </div>
  );

  const title = view === 'edit'
    ? (detail?.source === 'custom' ? 'Edit Framework' : 'New Framework')
    : view === 'create'
      ? 'New Framework'
      : view === 'export'
        ? 'Export custom Frameworks'
        : view === 'detail'
          ? detail?.framework_name || 'Framework'
          : 'Choose a Framework';

  const pickerBody = (
        <div className={`fw-picker fw-picker--${breakpoint}${embedded ? ' fw-picker--room' : ''}`}>
          {view === 'browse' && (
            <>
              <div
                className={`fw-chrome${embedded ? ' fw-chrome--titled' : ''}`}
                role="toolbar"
                aria-label="Framework chrome"
              >
                {embedded && (
                  <div className="fw-chrome__title">
                    <span className="fw-chrome__kicker">The Book</span>
                    <h1>{FRAMEWORK_BUILDER_HEADING}</h1>
                    <p className="fw-chrome__def">{FRAMEWORK_BUILDER_DEFINITION}</p>
                    {!selectedId && (
                      <FrameworkExplainerLink storyId={openBookId} />
                    )}
                  </div>
                )}
                <input
                  className="fw-input fw-input--search"
                  type="search"
                  placeholder="Search Frameworks"
                  value={search}
                  onChange={(e) => {
                    const next = e.target.value;
                    setSearch(next);
                    if (next.trim()) {
                      shelfDefaulted.current = true;
                      setCategory('all');
                    }
                  }}
                  aria-label="Search Frameworks"
                />
                <FrameworkChromeMenu
                  canExport={catalog.custom.length > 0}
                  onImport={handleImport}
                  onExport={() => {
                    if (!catalog.custom.length) return;
                    setExportIds(new Set(catalog.custom.map((item) => item.id)));
                    setView('export');
                  }}
                />
              </div>

              {loading && (
                <div className="fw-catalog-status" role="status" aria-live="polite" aria-busy="true">
                  <p className="fw-muted">{FRAMEWORKS_LOADING}</p>
                  {buildActions}
                </div>
              )}
              {error && <p className="fw-error" role="alert">{error}</p>}
              {notice && !reservePreview && (
                <p className="fw-notice" role="status" data-testid="fw-notice">{notice}</p>
              )}

              {!loading && (
                <div className={`fw-browse fw-browse--${breakpoint}`}>
                  <nav className="fw-col fw-col--cats" aria-label="Framework categories">
                    <div className="fw-col__scroll">
                      {categories.map((cat) => (
                        <button
                          key={cat.key}
                          type="button"
                          className={`fw-cat${category === cat.key ? ' fw-cat--selected' : ''}`}
                          aria-current={category === cat.key ? 'true' : undefined}
                          onClick={() => {
                            shelfDefaulted.current = true;
                            setCategory(cat.key);
                            const parent = genreParentFromCategory(cat.key);
                            setShelfTrail(parent ? [parent] : []);
                          }}
                        >
                          <span className="fw-cat__label">{cat.label}</span>
                          <span className="fw-cat__count">{cat.count}</span>
                        </button>
                      ))}
                    </div>
                  </nav>

                  <div className="fw-col fw-col--list">
                    {buildActions}
                    <div className="fw-col__scroll">
                      {showGenreDrill ? (
                        <ShelfBrowser
                          items={matchingPacks}
                          trail={drillTrail}
                          sharedPaths={sharedPaths}
                          activeItem={activeItem}
                          selectedId={selectedId}
                          onTrail={setShelfTrail}
                          onOpenPack={handleBrowseSelect}
                        />
                      ) : visibleItems.length === 0 ? (
                        <p className="fw-muted fw-col__empty">
                          {query
                            ? 'No Frameworks match.'
                            : category === 'custom'
                              ? 'Copies and Frameworks you create land here.'
                              : 'No Frameworks in this category.'}
                        </p>
                      ) : (
                        <ul className="fw-list">
                          {visibleItems.map((item) => (
                            <FrameworkRow
                              key={`${item.source}-${item.id}`}
                              item={item}
                              inUse={matchesSelected(item, selectedId)}
                              shared={Boolean(item.amazon_parent_path && sharedPaths.has(item.amazon_parent_path))}
                              selected={
                                (activeItem && activeItem.id === item.id && activeItem.source === item.source)
                                || (!activeItem && matchesSelected(item, selectedId))
                              }
                              onOpen={() => handleBrowseSelect(item)}
                            />
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>

                  {reservePreview && (
                    <div className="fw-col fw-col--preview" aria-live="polite">
                      {previewLoading && <p className="fw-muted">Loading…</p>}
                      {!previewLoading && detail && activeItem && (
                        <FrameworkDetail
                          dto={detail}
                          mode={mode}
                          hangKind={hangKind}
                          selectLabel={selectLabel}
                          selectHint={selectHint}
                          sharedShelf={Boolean(detail.amazon_parent_path && sharedPaths.has(detail.amazon_parent_path))}
                          embedded
                          notice={notice}
                          selecting={selecting}
                          asking={asking}
                          sequenceCount={sequenceCount}
                          onKeepSequences={() => commitSelect(true)}
                          onUseFrameworkSequences={() => commitSelect(false)}
                          onCancelSequences={() => setAsking(false)}
                          onSelect={mode === 'select' ? handleSelect : null}
                          onEdit={() => {
                            if (detail.source === 'library') {
                              startCopy(detail);
                            } else {
                              setView('edit');
                            }
                          }}
                          onCopy={() => startCopy(detail)}
                          onDelete={detail.source === 'custom' ? () => handleDelete(detail) : null}
                        />
                      )}
                      {!previewLoading && !(detail && activeItem) && (
                        <p className="fw-muted fw-preview-empty">{PREVIEW_EMPTY}</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {view === 'detail' && detail && (
            <FrameworkDetail
              dto={detail}
              mode={mode}
              hangKind={hangKind}
              selectLabel={selectLabel}
              selectHint={selectHint}
              sharedShelf={Boolean(detail.amazon_parent_path && sharedPaths.has(detail.amazon_parent_path))}
              onBack={() => { setAsking(false); setView('browse'); }}
              notice={notice}
              selecting={selecting}
              asking={asking}
              sequenceCount={sequenceCount}
              onKeepSequences={() => commitSelect(true)}
              onUseFrameworkSequences={() => commitSelect(false)}
              onCancelSequences={() => setAsking(false)}
              onSelect={mode === 'select' ? handleSelect : null}
              onEdit={() => {
                if (detail.source === 'library') {
                  startCopy(detail);
                } else {
                  setView('edit');
                }
              }}
              onCopy={() => startCopy(detail)}
              onDelete={detail.source === 'custom' ? () => handleDelete(detail) : null}
            />
          )}

          {view === 'create' && (
            <FrameworkCreateFlow
              manual={manualBuild}
              storyId={openBookId}
              onCancel={leaveBuild}
              onSave={(built) => handleSave(builtFrameworkSaveBody(built))}
              saving={busy}
              saveError={error}
            />
          )}

          {view === 'edit' && (
            <FrameworkEditor
              initial={detail}
              saving={busy}
              error={error}
              onCancel={() => setView(breakpoint === 'mobile' && detail?.beats?.length ? 'detail' : 'browse')}
              onSave={handleSave}
            />
          )}

          {view === 'export' && (
            <ExportPanel
              items={catalog.custom}
              selected={exportIds}
              onToggle={(id) => {
                setExportIds((prev) => {
                  const next = new Set(prev);
                  if (next.has(id)) next.delete(id);
                  else next.add(id);
                  return next;
                });
              }}
              onToggleAll={(on) => {
                setExportIds(on ? new Set(catalog.custom.map((i) => i.id)) : new Set());
              }}
              onCancel={() => setView('browse')}
              onExport={handleExport}
              busy={busy}
              error={error}
            />
          )}
        </div>
  );

  if (embedded) {
    if (!isOpen) return null;
    return (
      <>
        {pickerBody}
        <NameCopyDialog
          isOpen={!!copyTarget}
          originalName={copyTarget?.framework_name}
          title={copyTarget?.source === 'library' ? 'Edit creates a copy' : 'Name this copy'}
          confirmLabel={copyTarget?.source === 'library' ? 'Create copy and edit' : 'Create copy'}
          onCancel={() => setCopyTarget(null)}
          onConfirm={confirmCopy}
        />
      </>
    );
  }

  return (
    <>
      <AdaptiveModal
        isOpen={isOpen}
        onClose={closePicker}
        title={title}
        size="xl"
      >
        {pickerBody}
      </AdaptiveModal>

      <NameCopyDialog
        isOpen={!!copyTarget}
        originalName={copyTarget?.framework_name}
        title={copyTarget?.source === 'library' ? 'Edit creates a copy' : 'Name this copy'}
        confirmLabel={copyTarget?.source === 'library' ? 'Create copy and edit' : 'Create copy'}
        onCancel={() => setCopyTarget(null)}
        onConfirm={confirmCopy}
      />
    </>
  );
}

function FrameworkChromeMenu({ canExport, onImport, onExport }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="fw-chrome__more" ref={rootRef}>
      <button
        type="button"
        className="fw-btn fw-btn--ghost fw-chrome__more-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls="fw-chrome-menu"
        aria-label="More"
        onClick={() => setOpen((next) => !next)}
      >
        ⋯
      </button>
      <input
        ref={fileRef}
        type="file"
        accept="application/json,.json"
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (file) onImport(file);
        }}
      />
      {open && (
        <div className="fw-chrome__menu" id="fw-chrome-menu" role="menu" aria-label="Import and export">
          <button
            type="button"
            role="menuitem"
            className="fw-chrome__menu-item"
            onClick={() => {
              setOpen(false);
              fileRef.current?.click();
            }}
          >
            Import
          </button>
          <button
            type="button"
            role="menuitem"
            className="fw-chrome__menu-item"
            onClick={() => {
              if (!canExport) return;
              setOpen(false);
              onExport();
            }}
            disabled={!canExport}
            aria-describedby={canExport ? undefined : 'fw-export-why'}
          >
            Export
          </button>
          {!canExport && (
            <p id="fw-export-why" className="fw-export-why">{EXPORT_NEEDS_CUSTOM}</p>
          )}
        </div>
      )}
    </div>
  );
}

function FrameworkRow({ item, selected, inUse = false, onOpen, shared = false }) {
  const title = item.disambiguation_label
    ? (item.amazon_display_name || item.framework_name)
    : item.framework_name;
  return (
    <li className={`fw-row${selected ? ' fw-row--selected' : ''}${shared ? ' fw-row--shared' : ''}`}>
      <button
        type="button"
        className="fw-row__main"
        onClick={onOpen}
        aria-current={selected ? 'true' : undefined}
      >
        <span className="fw-row__identity">
          <span className="fw-row__name">
            {title}
            {inUse ? <span className="fw-row__inuse">In use</span> : null}
          </span>
          {item.disambiguation_label ? (
            <span className="fw-row__disambiguation">{item.disambiguation_label}</span>
          ) : null}
          {item.secondary_display ? (
            <span className="fw-row__secondary">Secondary: {item.secondary_display}</span>
          ) : null}
        </span>
        <span className="fw-row__meta">{item.beat_count} sequences</span>
      </button>
    </li>
  );
}

function ShelfBrowser({
  items,
  trail,
  sharedPaths,
  activeItem,
  selectedId,
  onTrail,
  onOpenPack,
}) {
  const level = shelfLevel(items, trail);
  const packs = level.packs;
  const sharedHere = packs.length > 1 && packs.every((item) => (
    item.amazon_parent_path && sharedPaths.has(item.amazon_parent_path)
  ));
  const drilled = trail.length > 0;

  return (
    <div className="fw-shelves">
      {drilled && (
        <nav className="fw-shelf-crumb" aria-label="Genre path">
          <ol>
            {trail.map((segment, index) => {
              const isLast = index === trail.length - 1;
              return (
                <li key={`${segment}-${index}`}>
                  {isLast ? (
                    <span aria-current="page">{segment}</span>
                  ) : (
                    <button
                      type="button"
                      className="fw-link"
                      onClick={() => onTrail(trail.slice(0, index + 1))}
                    >
                      {segment}
                    </button>
                  )}
                </li>
              );
            })}
          </ol>
        </nav>
      )}
      {trail.length <= 1 && (
        <p className="fw-muted fw-shelf-intro">
          {GENRE_DRILL_INTRO}
          <HelpIcon hintKey="B1" />
        </p>
      )}
      {!drilled && (
        <p className="fw-muted fw-col__empty">{PICK_A_GENRE}</p>
      )}
      {sharedHere && (
        <p className="fw-shelf-note" role="note">{SHARED_SHELF_NOTE}</p>
      )}
      {drilled && level.children.length > 0 && (
        <ul className="fw-list">
          {level.children.map((child) => (
            <li key={child.label} className="fw-row">
              <button
                type="button"
                className="fw-row__main"
                onClick={() => onTrail([...trail, child.label])}
              >
                <span className="fw-row__name">{child.label}</span>
                <span className="fw-row__meta">
                  {child.count} {child.count === 1 ? 'Framework' : 'Frameworks'}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {drilled && packs.length > 0 && (
        <ul className="fw-list">
          {packs.map((item) => (
            <FrameworkRow
              key={`${item.source || 'library'}-${item.id}`}
              item={item}
              inUse={matchesSelected(item, selectedId)}
              shared={Boolean(item.amazon_parent_path && sharedPaths.has(item.amazon_parent_path))}
              selected={
                (activeItem && activeItem.id === item.id && activeItem.source === item.source)
                || (!activeItem && matchesSelected(item, selectedId))
              }
              onOpen={() => onOpenPack(item)}
            />
          ))}
        </ul>
      )}
      {drilled && packs.length === 0 && level.children.length === 0 && (
        <p className="fw-muted fw-col__empty">{LISTING_EMPTY}</p>
      )}
    </div>
  );
}

function KeepSequencesDialog({
  count, frameworkName, onKeep, onUse, onCancel,
}) {
  const ref = useRef(null);
  useEffect(() => {
    const node = ref.current?.querySelector('button');
    if (node && typeof node.focus === 'function') node.focus();
  }, []);
  return (
    <div
      className="fw-keep"
      role="dialog"
      aria-modal="true"
      aria-labelledby="fw-keep-title"
      ref={ref}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.stopPropagation();
          onCancel();
        }
      }}
    >
      <h2 id="fw-keep-title">{KEEP_SEQUENCES_TITLE}</h2>
      <p>{KEEP_SEQUENCES_BODY(count, frameworkName)}</p>
      <button type="button" className="fw-keep__choice" onClick={onKeep}>
        <span className="fw-keep__name">{KEEP_MY_SEQUENCES}</span>
        <span className="fw-keep__help">{KEEP_MY_SEQUENCES_HELP}</span>
      </button>
      <button type="button" className="fw-keep__choice" onClick={onUse}>
        <span className="fw-keep__name">{USE_FRAMEWORK_SEQUENCES}</span>
        <span className="fw-keep__help">{USE_FRAMEWORK_SEQUENCES_HELP}</span>
      </button>
      <button type="button" className="fw-keep__cancel" onClick={onCancel}>
        {ADD_SCENE_CANCEL}
      </button>
    </div>
  );
}

function FrameworkDetail({
  dto, mode, embedded, hangKind, selectLabel, selectHint, sharedShelf, notice, onBack, onSelect, onEdit, onCopy, onDelete,
  selecting = false, asking = false, sequenceCount = 0,
  onKeepSequences, onUseFrameworkSequences, onCancelSequences,
}) {
  const groups = groupBeats(dto.beats);
  const actions = (
    <>
    {notice && <p className="fw-notice" role="status" data-testid="fw-notice">{notice}</p>}
    <div className="fw-dialog-actions">
      {onDelete && (
        <button type="button" className="fw-btn fw-btn--ghost" onClick={onDelete}>Delete</button>
      )}
      {dto.source === 'custom' && onCopy && (
        <button type="button" className="fw-btn fw-btn--ghost" onClick={onCopy}>Make a copy</button>
      )}
      {onEdit && (
        <button type="button" className="fw-btn fw-btn--ghost" onClick={onEdit}>
          {dto.source === 'library' ? 'Edit as a copy' : 'Edit'}
        </button>
      )}
    </div>
    </>
  );
  const showUse = mode === 'select' && onSelect;
  return (
    <div className={`fw-detail${embedded ? ' fw-detail--embedded' : ''}`}>
      {showUse && (
        <div className="fw-detail__use">
          <h3 className="fw-detail__name">
            {dto.framework_name}
            <HelpIcon hintKey="B2" />
          </h3>
          {asking ? (
            <KeepSequencesDialog
              count={sequenceCount}
              frameworkName={dto.framework_name || 'this Framework'}
              onKeep={onKeepSequences}
              onUse={onUseFrameworkSequences}
              onCancel={onCancelSequences}
            />
          ) : (
            <>
              <button
                type="button"
                className="fw-btn fw-btn--primary"
                onClick={onSelect}
                disabled={selecting}
                aria-busy={selecting || undefined}
              >
                {selecting ? USING_THIS_FRAMEWORK : (selectLabel || 'Use this Framework')}
              </button>
              <HelpIcon hintKey="B2" ariaLabel="Help: Does this fit my story?" />
            </>
          )}
        </div>
      )}
      <div className="fw-detail__scroll">
        {!embedded && onBack && (
          <button type="button" className="fw-link" onClick={onBack}>Back to list</button>
        )}
        {!showUse && (
          <h3 className="fw-detail__name">
            {dto.framework_name}
            <HelpIcon hintKey="B2" />
          </h3>
        )}
        {dto.disambiguation_label ? (
          <p className={`fw-detail__disambiguation${sharedShelf ? ' fw-detail__disambiguation--shared' : ''}`}>
            {dto.disambiguation_label}
          </p>
        ) : null}
        {dto.secondary_display ? (
          <p className="fw-detail__secondary">Secondary: {dto.secondary_display}</p>
        ) : null}
        {dto.amazon_parent_path ? (
          <p className="fw-muted fw-detail__shelf">{dto.amazon_parent_path}</p>
        ) : null}
        <p className="fw-detail__lede">{dto.description}</p>
        {selectHint && (
          <p className="fw-detail__hang-hint">
            {selectHint}
            <HelpIcon hintKey="S4" />
          </p>
        )}
        {hangKind === 'book' && !selectHint && (
          <p className="fw-detail__hang-hint">
            {THIS_BECOMES_THIS_BOOKS_FRAMEWORK}
            <HelpIcon hintKey="S4" />
          </p>
        )}
        <GenreStandingMark genre={dto.genre_tag} />
        <ThinGenreMarker show={Boolean(dto.thin_genre)} />
        {(dto.tracks || []).length > 0 && (
          <section className="fw-act">
            <h4 className="fw-act__title">Tracks</h4>
            <ul className="fw-act__beats">
              {dto.tracks.map((track) => (
                <li key={track.name}>
                  <strong>{track.name}</strong>
                  <p>
                    {track.purpose}
                    {(track.entry_position || track.payoff_position)
                      ? ` Enters ${track.entry_position || '?'}; pays off ${track.payoff_position || '?'}.`
                      : ''}
                  </p>
                </li>
              ))}
            </ul>
          </section>
        )}
        {(dto.tropes || []).length > 0 && (
          <section className="fw-act">
            <h4 className="fw-act__title">Tropes</h4>
            <ul className="fw-act__beats">
              {dto.tropes.map((trope) => (
                <li key={trope.name}>
                  <strong>{trope.name}</strong>
                  <p>
                    {trope.purpose}
                    {trope.checkable ? ' Coverage can look for this in conversation.' : ' Reference only — not scored.'}
                  </p>
                </li>
              ))}
            </ul>
          </section>
        )}
        {groups.map((group) => {
          const actRow = (dto.acts || []).find((row) => row.number === group.act);
          return (
            <section key={group.act} className="fw-act">
              <h4 className="fw-act__title">
                {outlineActHeader(actRow?.name, group.act)}
              </h4>
              {actRow?.purpose ? <p className="fw-muted">{actRow.purpose}</p> : null}
              <ol className="fw-act__beats">
                {group.beats.map((beat) => (
                  <li key={`${group.act}-${beat.name}`}>
                    <strong>{beat.name}</strong>
                    {beatCiteLabel(beat) !== 'Example' ? (
                      <span className="fw-cite">{beatCiteLabel(beat)}</span>
                    ) : null}
                    <p>{beat.purpose}</p>
                  </li>
                ))}
              </ol>
            </section>
          );
        })}
      </div>
      {actions}
    </div>
  );
}

function ExportPanel({ items, selected, onToggle, onToggleAll, onCancel, onExport, busy, error }) {
  const allOn = items.length > 0 && selected.size === items.length;
  return (
    <div className="fw-export">
      <p className="fw-muted">Only your Custom Frameworks can be exported. Choose which ones to include.</p>
      {items.length > 0 && (
        <label className="fw-check fw-check--all">
          <input
            type="checkbox"
            checked={allOn}
            onChange={(e) => onToggleAll(e.target.checked)}
          />
          Select all
        </label>
      )}
      <ul className="fw-export-list">
        {items.map((item) => (
          <li key={item.id}>
            <label className="fw-check">
              <input
                type="checkbox"
                checked={selected.has(item.id)}
                onChange={() => onToggle(item.id)}
              />
              {item.framework_name}
            </label>
          </li>
        ))}
      </ul>
      {error && <p className="fw-error" role="alert">{error}</p>}
      <div className="fw-dialog-actions">
        <button type="button" className="fw-btn fw-btn--ghost" onClick={onCancel}>Cancel</button>
        <button
          type="button"
          className="fw-btn fw-btn--primary"
          onClick={onExport}
          disabled={busy || selected.size === 0}
        >
          {busy ? 'Exporting…' : `Export ${selected.size || ''}`}
        </button>
      </div>
    </div>
  );
}


